// Drapé ponctuel des jupes et robes pour une pose statique (assise, au sol).
//
// Le skinning rigide laisse une jupe en "cône" raide et déchiquetée quand les cuisses se lèvent. Pour une pose
// donnée on lance UNE fois une mini-simulation (dynamique par positions : longueurs d'arêtes, gravité,
// collisions avec le corps posé, le siège et le sol, taille épinglée), puis on ré-exprime les positions obtenues
// dans l'espace de liaison du skinning : le GPU reproduit exactement le drapé, sans coût par image.
import * as THREE from 'three';

/** Siège (pouf cylindrique) en repère personnage : centre au sol, rayon, hauteur du dessus. */
export interface DrapeSeat { x: number; z: number; radius: number; height: number }

export interface SkinnedSource {
  /** Positions de liaison par sommet de rendu (xyz). */
  pos: ArrayLike<number>;
  normal?: ArrayLike<number>;
  skinIndex: ArrayLike<number>;
  skinWeight: ArrayLike<number>;
  count: number;
}

const _m = new THREE.Matrix4();
const _m2 = new THREE.Matrix4();
const _e = new Float32Array(16);

/** Matrice de skinning d'un sommet : Σ w_i · boneMatrices_i (dans `out`, 16 floats). */
function vertexMatrix(boneMatrices: Float32Array, si: ArrayLike<number>, sw: ArrayLike<number>, r: number, out: Float32Array) {
  out.fill(0);
  for (let k = 0; k < 4; k++) {
    const w = sw[r * 4 + k];
    if (w <= 0) continue;
    const o = si[r * 4 + k] * 16, B = boneMatrices;
    // Colonnes 0..2 (rotation/échelle) et 3 (translation) ; la dernière ligne reste (0, 0, 0, 1).
    out[0] += w * B[o]; out[1] += w * B[o + 1]; out[2] += w * B[o + 2];
    out[4] += w * B[o + 4]; out[5] += w * B[o + 5]; out[6] += w * B[o + 6];
    out[8] += w * B[o + 8]; out[9] += w * B[o + 9]; out[10] += w * B[o + 10];
    out[12] += w * B[o + 12]; out[13] += w * B[o + 13]; out[14] += w * B[o + 14];
  }
  out[15] = 1;
}

/** Skinning CPU de positions (et normales) : même calcul que le shader (bindMatrix = identité). */
export function skinPositions(src: SkinnedSource, boneMatrices: Float32Array, outPos: Float32Array, outNrm?: Float32Array) {
  for (let r = 0; r < src.count; r++) {
    vertexMatrix(boneMatrices, src.skinIndex, src.skinWeight, r, _e);
    const x = src.pos[r * 3], y = src.pos[r * 3 + 1], z = src.pos[r * 3 + 2];
    outPos[r * 3] = _e[0] * x + _e[4] * y + _e[8] * z + _e[12];
    outPos[r * 3 + 1] = _e[1] * x + _e[5] * y + _e[9] * z + _e[13];
    outPos[r * 3 + 2] = _e[2] * x + _e[6] * y + _e[10] * z + _e[14];
    if (outNrm && src.normal) {
      const nx = src.normal[r * 3], ny = src.normal[r * 3 + 1], nz = src.normal[r * 3 + 2];
      let ax = _e[0] * nx + _e[4] * ny + _e[8] * nz, ay = _e[1] * nx + _e[5] * ny + _e[9] * nz, az = _e[2] * nx + _e[6] * ny + _e[10] * nz;
      const l = Math.sqrt(ax * ax + ay * ay + az * az) || 1;
      ax /= l; ay /= l; az /= l;
      outNrm[r * 3] = ax; outNrm[r * 3 + 1] = ay; outNrm[r * 3 + 2] = az;
    }
  }
}

/** Grille dense de sommets (CSR) pour les requêtes "sommet du corps le plus proche". */
export class PointGrid {
  private readonly start: Int32Array;
  private readonly items: Int32Array;
  private readonly ox: number; private readonly oy: number; private readonly oz: number;
  private readonly nx: number; private readonly ny: number; private readonly nz: number;
  constructor(private readonly pos: Float32Array, n: number, private readonly cell = 0.025) {
    let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
    for (let i = 0; i < n; i++) {
      const x = pos[i * 3], y = pos[i * 3 + 1], z = pos[i * 3 + 2];
      if (x < x0) x0 = x; if (y < y0) y0 = y; if (z < z0) z0 = z;
      if (x > x1) x1 = x; if (y > y1) y1 = y; if (z > z1) z1 = z;
    }
    if (!n) { x0 = y0 = z0 = 0; x1 = y1 = z1 = 0; }
    this.ox = x0; this.oy = y0; this.oz = z0;
    this.nx = Math.max(1, Math.ceil((x1 - x0) / cell) + 1);
    this.ny = Math.max(1, Math.ceil((y1 - y0) / cell) + 1);
    this.nz = Math.max(1, Math.ceil((z1 - z0) / cell) + 1);
    const cells = this.nx * this.ny * this.nz;
    const cellOf = new Int32Array(n);
    const cnt = new Int32Array(cells + 1);
    for (let i = 0; i < n; i++) {
      const c = this.cellIndex(Math.floor((pos[i * 3] - x0) / cell), Math.floor((pos[i * 3 + 1] - y0) / cell), Math.floor((pos[i * 3 + 2] - z0) / cell));
      cellOf[i] = c;
      cnt[c + 1]++;
    }
    for (let c = 0; c < cells; c++) cnt[c + 1] += cnt[c];
    this.start = cnt;
    this.items = new Int32Array(n);
    const fill = cnt.slice(0, cells);
    for (let i = 0; i < n; i++) this.items[fill[cellOf[i]]++] = i;
  }
  private cellIndex(i: number, j: number, k: number) { return (k * this.ny + j) * this.nx + i; }
  /** Jusqu'à `k` plus proches à moins de `maxD` (triés) ; renvoie leur nombre. */
  kNearest(x: number, y: number, z: number, maxD: number, k: number, idx: Int32Array, d2: Float32Array): number {
    const c = this.cell, P = this.pos;
    const ci = Math.floor((x - this.ox) / c), cj = Math.floor((y - this.oy) / c), ck = Math.floor((z - this.oz) / c);
    const r = Math.ceil(maxD / c), lim = maxD * maxD;
    let m = 0;
    const i0 = Math.max(0, ci - r), i1 = Math.min(this.nx - 1, ci + r);
    const j0 = Math.max(0, cj - r), j1 = Math.min(this.ny - 1, cj + r);
    const k0 = Math.max(0, ck - r), k1 = Math.min(this.nz - 1, ck + r);
    for (let kk = k0; kk <= k1; kk++) for (let j = j0; j <= j1; j++) {
      const row = (kk * this.ny + j) * this.nx;
      const a = this.start[row + i0], b = this.start[row + i1 + 1];
      for (let t = a; t < b; t++) {
        const p = this.items[t] * 3;
        const dx = P[p] - x, dy = P[p + 1] - y, dz = P[p + 2] - z;
        const d = dx * dx + dy * dy + dz * dz;
        if (d >= lim || (m === k && d >= d2[k - 1])) continue;
        // Insertion triée.
        let q = m < k ? m++ : k - 1;
        while (q > 0 && d2[q - 1] > d) { d2[q] = d2[q - 1]; idx[q] = idx[q - 1]; q--; }
        d2[q] = d; idx[q] = this.items[t];
      }
    }
    return m;
  }
  nearest(x: number, y: number, z: number, maxD: number): number {
    const c = this.cell, P = this.pos;
    const ci = Math.floor((x - this.ox) / c), cj = Math.floor((y - this.oy) / c), ck = Math.floor((z - this.oz) / c);
    const r = Math.ceil(maxD / c);
    let best = -1, bd = maxD * maxD;
    const i0 = Math.max(0, ci - r), i1 = Math.min(this.nx - 1, ci + r);
    const j0 = Math.max(0, cj - r), j1 = Math.min(this.ny - 1, cj + r);
    const k0 = Math.max(0, ck - r), k1 = Math.min(this.nz - 1, ck + r);
    for (let k = k0; k <= k1; k++) for (let j = j0; j <= j1; j++) {
      const row = (k * this.ny + j) * this.nx;
      const a = this.start[row + i0], b = this.start[row + i1 + 1];
      for (let t = a; t < b; t++) {
        const p = this.items[t] * 3;
        const dx = P[p] - x, dy = P[p + 1] - y, dz = P[p + 2] - z;
        const d = dx * dx + dy * dy + dz * dz;
        if (d < bd) { bd = d; best = this.items[t]; }
      }
    }
    return best;
  }
}

/** Diagnostic du dernier drapé (fractions de sommets dans le corps / d'arêtes déchirées). */
export const lastDrapeStats = { inside: 0, torn: 0, rejected: false, ms: 0, method: 'none' as 'none' | 'sweep' | 'transfer', sweepRejected: false };
/**
 * Réglages (ajustables en dev). `sweep` : balayage cinématique (le corps passe progressivement de la pose de repos à
 * la pose voulue, le tissu est poussé au fur et à mesure) ; `substeps` : étapes du balayage, `subIters` : itérations
 * par étape ; `insideProbe` : portée (m) du contrôle « tissu dans le corps ».
 */
export const drapeTuning = {
  iterations: 70, passes: 4, bend: 0.35, smooth: 6, gravity: 0.0035, layerMax: 0.04, maxInside: 0.03, maxTorn: 0.16, tear: 1.3,
  /** Gravité multipliée pour le tissu sans corps à moins de 5 cm (ourlets qui pendent : ils retombent au lieu de rester
   *  en tablette) ; passes de lissage du déplacement du transfert. */
  hangGravity: 2.2, transferSmooth: 6, transferK: 1,
  /** Mini-jupes assises sur un siège (ourlet au repos plus de `sweepAboveKnee` m au-dessus du genou) : balayage (voir
   *  HumanImpl.drape). Les robes au genou passent par le transfert : le balayage y fait percer les genoux. */
  sweepShortSeated: true, sweepAboveKnee: 0.07,
  /** Robes au genou ou plus longues, assises sur un siège : pas de drapé (le skinning suit les cuisses et reste lisse ;
   *  le transfert y laisse des lambeaux sur le siège et froisse le devant). */
  rawSeatedLong: true,
  sweep: false, substeps: 18, subIters: 3, settle: 45, insideProbe: 0.1,
  /** Départ par transfert de surface (voir drapeClothSteps) ; `transferNear`/`transferFar` : distance au corps (m) en
   *  deçà de laquelle le tissu est replacé sur la peau posée, au-delà de laquelle il garde son skinning. */
  transfer: true, transferNear: 0.1, transferFar: 0.2,
};

/** Corps : positions/normales posées (pose courante) et de liaison (repos), en sommets de rendu. */
export interface DrapeBody {
  pos: Float32Array; nrm: Float32Array;
  bindPos: ArrayLike<number>; bindNrm: ArrayLike<number>;
  count: number;
  /** Sommets du corps pouvant porter le tissu au départ (transfert de surface) : jambes, bassin, buste — pas les
   *  bras ni les mains (sinon la jupe suivrait les mains posées sur les genoux). Absent : tous. */
  anchor?: Uint8Array;
}

export interface DrapeCloth {
  /** Positions de liaison (ajustées) par sommet .obj. */
  objPos: Float32Array;
  nVerts: number;
  tris: Uint32Array;
  /** Sommet de rendu → sommet .obj. */
  r2v: ArrayLike<number>;
  skinIndex: ArrayLike<number>;
  skinWeight: ArrayLike<number>;
  /** Sommets libres (jupe) ; les autres sont épinglés à leur position skinnée. */
  free: Uint8Array;
}

/**
 * Simule le drapé et renvoie les nouvelles positions de LIAISON par sommet .obj (ou null si rien à faire).
 * body : positions/normales du corps déjà skinnées (pose courante), en sommets de rendu.
 */
export function drapeCloth(
  cloth: DrapeCloth, boneMatrices: Float32Array, body: DrapeBody,
  seat: DrapeSeat | null, iterations = drapeTuning.iterations,
): Float32Array | null {
  const job = drapeClothSteps(cloth, boneMatrices, body, seat, iterations);
  for (;;) { const r = job.next(); if (r.done) return r.value; }
}

/** Même calcul, découpé : un `yield` par itération (étalé sur plusieurs images, sans à-coup). */
export function* drapeClothSteps(
  cloth: DrapeCloth, boneMatrices: Float32Array, body: DrapeBody,
  seat: DrapeSeat | null, iterations = drapeTuning.iterations,
): Generator<void, Float32Array | null> {
  const T = drapeTuning;
  let spent = 0, tLap = performance.now();
  const { pos: bodyPos, nrm: bodyNrm, count: bodyCount } = body;
  const n = cloth.nVerts;
  let anyFree = false;
  for (let v = 0; v < n; v++) if (cloth.free[v]) { anyFree = true; break; }
  if (!anyFree) return null;
  // Premier sommet de rendu de chaque sommet .obj (poids de skinning par sommet .obj).
  const firstR = new Int32Array(n).fill(-1);
  for (let r = 0; r < cloth.r2v.length; r++) if (firstR[cloth.r2v[r]] < 0) firstR[cloth.r2v[r]] = r;
  // Matrices de skinning par sommet .obj et positions posées.
  const M = new Float32Array(n * 16);
  const X = new Float32Array(n * 3);
  for (let v = 0; v < n; v++) {
    const r = firstR[v];
    if (r < 0) continue;
    vertexMatrix(boneMatrices, cloth.skinIndex, cloth.skinWeight, r, _e);
    M.set(_e, v * 16);
    const x = cloth.objPos[v * 3], y = cloth.objPos[v * 3 + 1], z = cloth.objPos[v * 3 + 2];
    X[v * 3] = _e[0] * x + _e[4] * y + _e[8] * z + _e[12];
    X[v * 3 + 1] = _e[1] * x + _e[5] * y + _e[9] * z + _e[13];
    X[v * 3 + 2] = _e[2] * x + _e[6] * y + _e[10] * z + _e[14];
  }
  // Arêtes et longueurs au repos (dans l'espace de liaison : le tissu ne s'étire pas), plus contraintes de
  // flexion entre les sommets opposés de deux triangles voisins (raideur du tissu : il ne se retourne pas).
  const firstOpp = new Map<number, number>();
  const edges: number[] = [];
  const bends: number[] = [];
  const P0 = cloth.objPos;
  for (let t = 0; t < cloth.tris.length; t += 3) {
    for (let k = 0; k < 3; k++) {
      const a = cloth.tris[t + k], b = cloth.tris[t + ((k + 1) % 3)], c = cloth.tris[t + ((k + 2) % 3)];
      const lo = Math.min(a, b), hi = Math.max(a, b), key = lo * n + hi;
      const opp = firstOpp.get(key);
      if (opp === undefined) {
        firstOpp.set(key, c);
        if (cloth.free[a] || cloth.free[b]) edges.push(a, b);
      } else if (opp !== c && (cloth.free[opp] || cloth.free[c])) bends.push(opp, c);
    }
  }
  const E = Uint32Array.from(edges);
  const nE = E.length / 2;
  const rest = new Float32Array(nE);
  for (let e = 0; e < nE; e++) {
    const a = E[e * 2] * 3, b = E[e * 2 + 1] * 3;
    const dx = P0[a] - P0[b], dy = P0[a + 1] - P0[b + 1], dz = P0[a + 2] - P0[b + 2];
    rest[e] = Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
  const Bd = Uint32Array.from(bends);
  const nB = Bd.length / 2;
  const bendRest = new Float32Array(nB);
  for (let e = 0; e < nB; e++) {
    const a = Bd[e * 2] * 3, b = Bd[e * 2 + 1] * 3;
    const dx = P0[a] - P0[b], dy = P0[a + 1] - P0[b + 1], dz = P0[a + 2] - P0[b + 2];
    bendRest[e] = Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
  const free = cloth.free;
  /** Contrainte de distance (Gauss-Seidel) de raideur k. */
  const solve = (I: Uint32Array, R: Float32Array, count: number, k: number) => {
    for (let e = 0; e < count; e++) {
      const a = I[e * 2], b = I[e * 2 + 1];
      const fa = free[a], fb = free[b];
      const ax = a * 3, bx = b * 3;
      const dx = X[bx] - X[ax], dy = X[bx + 1] - X[ax + 1], dz = X[bx + 2] - X[ax + 2];
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1e-9;
      const diff = k * (d - R[e]) / d;
      const wa = fa ? (fb ? 0.5 : 1) : 0, wb = fb ? (fa ? 0.5 : 1) : 0;
      X[ax] += dx * diff * wa; X[ax + 1] += dy * diff * wa; X[ax + 2] += dz * diff * wa;
      X[bx] -= dx * diff * wb; X[bx + 1] -= dy * diff * wb; X[bx + 2] -= dz * diff * wb;
    }
  };
  // Écart de chaque sommet à la peau au repos (couches superposées : volants, doublure), plafonné : le drapé le
  // conserve pour que les couches ne se traversent pas.
  const layer = new Float32Array(n);
  {
    let a0 = Infinity, b0 = Infinity, c0 = Infinity, a1 = -Infinity, b1 = -Infinity, c1 = -Infinity;
    for (let v = 0; v < n; v++) {
      if (!cloth.free[v]) continue;
      const o = v * 3;
      a0 = Math.min(a0, P0[o]); b0 = Math.min(b0, P0[o + 1]); c0 = Math.min(c0, P0[o + 2]);
      a1 = Math.max(a1, P0[o]); b1 = Math.max(b1, P0[o + 1]); c1 = Math.max(c1, P0[o + 2]);
    }
    const BP = body.bindPos, BN = body.bindNrm;
    const sel: number[] = [];
    for (let b = 0; b < bodyCount; b++) {
      const x = BP[b * 3], y = BP[b * 3 + 1], z = BP[b * 3 + 2];
      if (x > a0 - 0.06 && x < a1 + 0.06 && y > b0 - 0.06 && y < b1 + 0.06 && z > c0 - 0.06 && z < c1 + 0.06) sel.push(b);
    }
    const sp = new Float32Array(sel.length * 3);
    sel.forEach((b, i) => { sp[i * 3] = BP[b * 3]; sp[i * 3 + 1] = BP[b * 3 + 1]; sp[i * 3 + 2] = BP[b * 3 + 2]; });
    const bg = new PointGrid(sp, sel.length, 0.03);
    for (let v = 0; v < n; v++) {
      if (!cloth.free[v]) continue;
      const o = v * 3, i = bg.nearest(P0[o], P0[o + 1], P0[o + 2], 0.06);
      if (i < 0) { layer[v] = T.layerMax; continue; }
      const b = sel[i];
      const sd = (P0[o] - BP[b * 3]) * BN[b * 3] + (P0[o + 1] - BP[b * 3 + 1]) * BN[b * 3 + 1] + (P0[o + 2] - BP[b * 3 + 2]) * BN[b * 3 + 2];
      layer[v] = Math.min(T.layerMax, Math.max(0, sd));
    }
  }
  spent += performance.now() - tLap;
  yield;
  tLap = performance.now();
  // Départ par transfert de surface : le skinning rigide d'une jupe mêle cuisses et bassin, ce qui tasse son devant dans
  // le pli de l'aine quand les cuisses se lèvent (tissu DANS les cuisses, hors de portée des collisions). Chaque sommet
  // libre proche du corps au repos est replacé à la même distance, le long de la normale, du même point de peau dans la
  // pose voulue (cuisse, hanche, tibia) : le devant se retrouve sur les cuisses, le dos sous les fesses, sans
  // interpénétration ; loin du corps (ourlet d'une jupe ample), on garde le skinning, avec une transition douce.
  if (T.transfer) {
    const BP = body.bindPos, BN = body.bindNrm, A = body.anchor;
    let a0 = Infinity, b0 = Infinity, c0 = Infinity, a1 = -Infinity, b1 = -Infinity, c1 = -Infinity;
    for (let v = 0; v < n; v++) {
      if (!cloth.free[v]) continue;
      const o = v * 3;
      a0 = Math.min(a0, P0[o]); b0 = Math.min(b0, P0[o + 1]); c0 = Math.min(c0, P0[o + 2]);
      a1 = Math.max(a1, P0[o]); b1 = Math.max(b1, P0[o + 1]); c1 = Math.max(c1, P0[o + 2]);
    }
    const R = T.transferFar;
    const sel: number[] = [];
    for (let b = 0; b < bodyCount; b++) {
      if (A && !A[b]) continue;
      const x = BP[b * 3], y = BP[b * 3 + 1], z = BP[b * 3 + 2];
      if (x > a0 - R && x < a1 + R && y > b0 - R && y < b1 + R && z > c0 - R && z < c1 + R) sel.push(b);
    }
    const sp = new Float32Array(sel.length * 3);
    sel.forEach((b, i) => { sp[i * 3] = BP[b * 3]; sp[i * 3 + 1] = BP[b * 3 + 1]; sp[i * 3 + 2] = BP[b * 3 + 2]; });
    const ag = new PointGrid(sp, sel.length, 0.04);
    // Cible : moyenne pondérée (inverse du carré de la distance) de quelques points de peau tournés vers le sommet —
    // un seul « plus proche » ferait hésiter le milieu du devant entre cuisse gauche et cuisse droite (fente).
    const K = Math.max(1, T.transferK | 0), kIdx = new Int32Array(K), kD2 = new Float32Array(K);
    const Xs = new Float32Array(X); // skinning rigide (départ de l'ancien drapé)
    for (let v = 0; v < n; v++) {
      if ((v & 127) === 0 && performance.now() - tLap > 4) { spent += performance.now() - tLap; yield; tLap = performance.now(); }
      if (!cloth.free[v]) continue;
      const o = v * 3, x = P0[o], y = P0[o + 1], z = P0[o + 2];
      let m = ag.kNearest(x, y, z, 0.08, K, kIdx, kD2);
      if (!m) m = ag.kNearest(x, y, z, R, K, kIdx, kD2);
      if (!m) continue;
      let tx = 0, ty = 0, tz = 0, tw = 0;
      for (let q = 0; q < m; q++) {
        const b = sel[kIdx[q]];
        const dx = x - BP[b * 3], dy = y - BP[b * 3 + 1], dz = z - BP[b * 3 + 2];
        const sdRaw = dx * BN[b * 3] + dy * BN[b * 3 + 1] + dz * BN[b * 3 + 2];
        if (sdRaw < -0.005 && q > 0) continue; // peau tournée de l'autre côté (face interne de l'autre jambe)
        const sd = Math.min(R, Math.max(0.004, sdRaw));
        const wq = 1 / (kD2[q] + 1e-4);
        tx += (bodyPos[b * 3] + bodyNrm[b * 3] * sd) * wq;
        ty += (bodyPos[b * 3 + 1] + bodyNrm[b * 3 + 1] * sd) * wq;
        tz += (bodyPos[b * 3 + 2] + bodyNrm[b * 3 + 2] * sd) * wq;
        tw += wq;
      }
      if (tw <= 0) continue;
      const dist = Math.sqrt(kD2[0]);
      const w = dist <= T.transferNear ? 0 : dist >= R ? 1 : (() => { const u = (dist - T.transferNear) / (R - T.transferNear); return u * u * (3 - 2 * u); })();
      if (w >= 1) continue;
      tx /= tw; ty /= tw; tz /= tw;
      X[o] = tx + (X[o] - tx) * w; X[o + 1] = ty + (X[o + 1] - ty) * w; X[o + 2] = tz + (X[o + 2] - tz) * w;
    }
    // Lissage du déplacement (X − skinning) le long du tissu : pas de couture entre parties transférées et non
    // transférées, ni entre voisins attachés à des jambes différentes ; la ceinture épinglée sert de bord.
    const D = new Float32Array(n * 3), acc = new Float32Array(n * 3), deg = new Float32Array(n);
    for (let v = 0; v < n * 3; v++) D[v] = X[v] - Xs[v];
    for (let e = 0; e < nE; e++) { deg[E[e * 2]]++; deg[E[e * 2 + 1]]++; }
    for (let pass = 0; pass < T.transferSmooth; pass++) {
      acc.fill(0);
      for (let e = 0; e < nE; e++) {
        const a = E[e * 2] * 3, b = E[e * 2 + 1] * 3;
        for (let c = 0; c < 3; c++) { acc[a + c] += D[b + c]; acc[b + c] += D[a + c]; }
      }
      for (let v = 0; v < n; v++) {
        if (!cloth.free[v] || !deg[v]) continue;
        const o = v * 3;
        for (let c = 0; c < 3; c++) D[o + c] = 0.5 * D[o + c] + 0.5 * acc[o + c] / deg[v];
      }
    }
    for (let v = 0; v < n; v++) {
      if (!cloth.free[v]) continue;
      const o = v * 3;
      X[o] = Xs[o] + D[o]; X[o + 1] = Xs[o + 1] + D[o + 1]; X[o + 2] = Xs[o + 2] + D[o + 2];
    }
  }
  // Corps utile : sommets proches de la boîte englobante du tissu (jambes, hanches), dans une grille fine.
  let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
  for (let v = 0; v < n; v++) {
    if (!cloth.free[v]) continue;
    const o = v * 3;
    x0 = Math.min(x0, X[o]); y0 = Math.min(y0, X[o + 1]); z0 = Math.min(z0, X[o + 2]);
    x1 = Math.max(x1, X[o]); y1 = Math.max(y1, X[o + 1]); z1 = Math.max(z1, X[o + 2]);
  }
  const M8 = 0.25;   // marge : le tissu peut tomber jusqu'à ~25 cm sous sa position skinnée
  const keep: number[] = [];
  for (let b = 0; b < bodyCount; b++) {
    const x = bodyPos[b * 3], y = bodyPos[b * 3 + 1], z = bodyPos[b * 3 + 2];
    if (x > x0 - 0.06 && x < x1 + 0.06 && y > y0 - M8 && y < y1 + 0.06 && z > z0 - 0.06 && z < z1 + 0.06) keep.push(b);
  }
  const bp = new Float32Array(keep.length * 3), bn = new Float32Array(keep.length * 3);
  keep.forEach((b, i) => {
    bp[i * 3] = bodyPos[b * 3]; bp[i * 3 + 1] = bodyPos[b * 3 + 1]; bp[i * 3 + 2] = bodyPos[b * 3 + 2];
    bn[i * 3] = bodyNrm[b * 3]; bn[i * 3 + 1] = bodyNrm[b * 3 + 1]; bn[i * 3 + 2] = bodyNrm[b * 3 + 2];
  });
  const grid = new PointGrid(bp, keep.length, 0.025);
  // Sommet du corps le plus proche, mis en cache et rafraîchi toutes les quelques itérations.
  const near = new Int32Array(n).fill(-1);
  const gap = 0.004;
  const seatR = seat ? seat.radius : 0;
  for (let it = 0; it < iterations; it++) {
    // Gravité (décroissante pour converger).
    const g = T.gravity * (1 - it / iterations);
    for (let v = 0; v < n; v++) if (cloth.free[v]) X[v * 3 + 1] -= near[v] < 0 ? g * T.hangGravity : g;
    // Longueurs et flexion (quelques passes de Gauss-Seidel).
    for (let pass = 0; pass < T.passes; pass++) {
      solve(E, rest, nE, 1);
      solve(Bd, bendRest, nB, T.bend);
    }
    // Collisions : corps posé, siège, sol.
    const refresh = it % 5 === 0 || it === iterations - 1;
    for (let v = 0; v < n; v++) {
      if (!cloth.free[v]) continue;
      const o = v * 3;
      if (refresh) near[v] = grid.nearest(X[o], X[o + 1], X[o + 2], 0.05);
      const b = near[v];
      if (b >= 0) {
        const nx = bn[b * 3], ny = bn[b * 3 + 1], nz = bn[b * 3 + 2];
        const s = (X[o] - bp[b * 3]) * nx + (X[o + 1] - bp[b * 3 + 1]) * ny + (X[o + 2] - bp[b * 3 + 2]) * nz;
        const want = gap + layer[v];
        if (s < want && s > -0.05) { const p = want - s; X[o] += p * nx; X[o + 1] += p * ny; X[o + 2] += p * nz; }
      }
      // Siège. Le tissu pris SOUS le corps (fesses, dessous des cuisses : normale de peau tournée vers le bas, peau
      // au-dessus) n'est pas remonté sur le dessus : la peau elle-même s'enfonce de quelques centimètres sous la hauteur
      // d'assise mesurée, et la pince le renverrait dans le corps (conflit qui étire le tissu en lanières).
      const under = b >= 0 && bn[b * 3 + 1] < -0.25 && bp[b * 3 + 1] > X[o + 1] - 0.01;
      if (seat && !under) {
        const dx = X[o] - seat.x, dz = X[o + 2] - seat.z, rr = Math.sqrt(dx * dx + dz * dz);
        const top = seat.height + gap + layer[v];
        if (rr < seatR + gap && X[o + 1] < top) {
          // Près du dessus : posé sur le siège ; sinon repoussé sur le côté.
          if (top - X[o + 1] < 0.06) X[o + 1] = top;
          else { const k = (seatR + gap) / (rr || 1e-6); X[o] = seat.x + dx * k; X[o + 2] = seat.z + dz * k; }
        }
      }
      const fl = 0.003 + layer[v];
      if (X[o + 1] < fl) X[o + 1] = fl;
    }
    spent += performance.now() - tLap;
    yield;
    tLap = performance.now();
  }
  // Lissage de Taubin (sans rétrécissement) : efface le froissé haute fréquence (tissu entassé au sol, dans le
  // giron) ; puis une dernière passe de collisions.
  {
    const deg = new Float32Array(n);
    for (let e = 0; e < nE; e++) { deg[E[e * 2]]++; deg[E[e * 2 + 1]]++; }
    const acc = new Float32Array(n * 3);
    const smooth = (f: number) => {
      acc.fill(0);
      for (let e = 0; e < nE; e++) {
        const a = E[e * 2] * 3, b = E[e * 2 + 1] * 3;
        for (let k = 0; k < 3; k++) { const d = X[b + k] - X[a + k]; acc[a + k] += d; acc[b + k] -= d; }
      }
      for (let v = 0; v < n; v++) {
        if (!free[v] || !deg[v]) continue;
        const o = v * 3, w = f / deg[v];
        X[o] += acc[o] * w; X[o + 1] += acc[o + 1] * w; X[o + 2] += acc[o + 2] * w;
      }
    };
    for (let i = 0; i < T.smooth; i++) { smooth(0.5); smooth(-0.53); }
    for (let v = 0; v < n; v++) {
      if (!free[v]) continue;
      const o = v * 3, b = grid.nearest(X[o], X[o + 1], X[o + 2], 0.05);
      near[v] = b;
      if (b >= 0) {
        const nx = bn[b * 3], ny = bn[b * 3 + 1], nz = bn[b * 3 + 2];
        const s = (X[o] - bp[b * 3]) * nx + (X[o + 1] - bp[b * 3 + 1]) * ny + (X[o + 2] - bp[b * 3 + 2]) * nz;
        const want = gap + layer[v];
        if (s < want && s > -0.05) { const p = want - s; X[o] += p * nx; X[o + 1] += p * ny; X[o + 2] += p * nz; }
      }
      const fl = 0.003 + layer[v];
      if (X[o + 1] < fl) X[o + 1] = fl;
    }
  }
  // Contrôle qualité : tissu resté dans le corps ou déchiré (membres serrés, genoux remontés…) → on renonce.
  let inside = 0, torn = 0, nFree = 0, bad = false;
  // Sonde large seulement là où aucun sommet du corps n'est à moins de 5 cm (tissu au cœur d'une cuisse).
  let probe: PointGrid | null = null;
  for (let v = 0; v < n; v++) {
    if (!cloth.free[v]) continue;
    nFree++;
    const o = v * 3;
    if (!Number.isFinite(X[o] + X[o + 1] + X[o + 2])) { bad = true; break; }
    let b = near[v];
    if (b < 0) b = (probe ??= new PointGrid(bp, keep.length, 0.05)).nearest(X[o], X[o + 1], X[o + 2], T.insideProbe);
    if (b < 0) continue;
    const s = (X[o] - bp[b * 3]) * bn[b * 3] + (X[o + 1] - bp[b * 3 + 1]) * bn[b * 3 + 1] + (X[o + 2] - bp[b * 3 + 2]) * bn[b * 3 + 2];
    if (s < -0.006) inside++;
  }
  let nCounted = 0;
  for (let e = 0; e < nE; e++) {
    if (rest[e] < 0.003) continue; // fronces : arêtes minuscules, sans effet visible
    nCounted++;
    const a = E[e * 2] * 3, b = E[e * 2 + 1] * 3;
    const dx = X[b] - X[a], dy = X[b + 1] - X[a + 1], dz = X[b + 2] - X[a + 2];
    if (Math.sqrt(dx * dx + dy * dy + dz * dz) > rest[e] * T.tear + 0.002) torn++;
  }
  lastDrapeStats.inside = inside / Math.max(1, nFree);
  lastDrapeStats.torn = torn / Math.max(1, nCounted);
  lastDrapeStats.rejected = bad || lastDrapeStats.inside > T.maxInside || lastDrapeStats.torn > T.maxTorn;
  lastDrapeStats.ms = spent + performance.now() - tLap;
  if (lastDrapeStats.rejected) return null;
  // Retour dans l'espace de liaison : B = M⁻¹ · X.
  const out = new Float32Array(cloth.objPos);
  for (let v = 0; v < n; v++) {
    if (!cloth.free[v] || firstR[v] < 0) continue;
    _m.fromArray(M, v * 16).invert();
    const e = _m.elements, x = X[v * 3], y = X[v * 3 + 1], z = X[v * 3 + 2];
    out[v * 3] = e[0] * x + e[4] * y + e[8] * z + e[12];
    out[v * 3 + 1] = e[1] * x + e[5] * y + e[9] * z + e[13];
    out[v * 3 + 2] = e[2] * x + e[6] * y + e[10] * z + e[14];
  }
  return out;
}

/** Corps animé pour le balayage : source skinnée (positions/normales de liaison, poids). */
export interface SweepBody { src: SkinnedSource & { normal: ArrayLike<number> } }

/** Skinning CPU d'un sous-ensemble de sommets du corps (positions et normales compactes). */
function skinSubset(src: SkinnedSource & { normal: ArrayLike<number> }, mats: Float32Array, idx: Int32Array, pos: Float32Array, nrm: Float32Array) {
  for (let i = 0; i < idx.length; i++) {
    const r = idx[i];
    vertexMatrix(mats, src.skinIndex, src.skinWeight, r, _e);
    const x = src.pos[r * 3], y = src.pos[r * 3 + 1], z = src.pos[r * 3 + 2];
    pos[i * 3] = _e[0] * x + _e[4] * y + _e[8] * z + _e[12];
    pos[i * 3 + 1] = _e[1] * x + _e[5] * y + _e[9] * z + _e[13];
    pos[i * 3 + 2] = _e[2] * x + _e[6] * y + _e[10] * z + _e[14];
    const nx = src.normal[r * 3], ny = src.normal[r * 3 + 1], nz = src.normal[r * 3 + 2];
    const ax = _e[0] * nx + _e[4] * ny + _e[8] * nz, ay = _e[1] * nx + _e[5] * ny + _e[9] * nz, az = _e[2] * nx + _e[6] * ny + _e[10] * nz;
    const l = Math.sqrt(ax * ax + ay * ay + az * az) || 1;
    nrm[i * 3] = ax / l; nrm[i * 3 + 1] = ay / l; nrm[i * 3 + 2] = az / l;
  }
}

/** Positions skinnées des sommets .obj du tissu pour une série de matrices. */
function skinCloth(cloth: DrapeCloth, firstR: Int32Array, mats: Float32Array, out: Float32Array) {
  const n = cloth.nVerts, P = cloth.objPos;
  for (let v = 0; v < n; v++) {
    const r = firstR[v];
    if (r < 0) continue;
    vertexMatrix(mats, cloth.skinIndex, cloth.skinWeight, r, _e);
    const x = P[v * 3], y = P[v * 3 + 1], z = P[v * 3 + 2];
    out[v * 3] = _e[0] * x + _e[4] * y + _e[8] * z + _e[12];
    out[v * 3 + 1] = _e[1] * x + _e[5] * y + _e[9] * z + _e[13];
    out[v * 3 + 2] = _e[2] * x + _e[6] * y + _e[10] * z + _e[14];
  }
}

/**
 * Drapé par balayage cinématique. Le skinning rigide d'une jupe accrochée au bassin la fait traverser par les cuisses
 * qui se lèvent (assise) : le tissu se retrouve DANS le corps, hors de portée d'une collision locale. Ici on part de la
 * pose de repos (tissu tel que dessiné, hors du corps) et on amène le squelette jusqu'à la pose voulue en plusieurs
 * étapes (interpolation hiérarchique, faite par l'appelant) : à chaque étape le tissu suit son skinning (incrément
 * rigide), puis longueurs, flexion et collisions le remettent en forme ; les cuisses le poussent peu à peu sur les
 * genoux. Enfin, gravité et repos comme le drapé simple. La dernière matrice de frames est la pose voulue.
 */
export function* drapeSweepSteps(
  cloth: DrapeCloth, frames: Float32Array[], body: SweepBody, seat: DrapeSeat | null, carryBone = -1,
): Generator<void, Float32Array | null> {
  const T = drapeTuning;
  let spent = 0, tLap = performance.now();
  const n = cloth.nVerts, free = cloth.free, P0 = cloth.objPos;
  let anyFree = false;
  for (let v = 0; v < n; v++) if (free[v]) { anyFree = true; break; }
  if (!anyFree) return null;
  const K = frames.length - 1;
  const firstR = new Int32Array(n).fill(-1);
  for (let r = 0; r < cloth.r2v.length; r++) if (firstR[cloth.r2v[r]] < 0) firstR[cloth.r2v[r]] = r;
  // Arêtes, flexion, longueurs au repos (espace de liaison).
  const firstOpp = new Map<number, number>();
  const edges: number[] = [], bends: number[] = [];
  for (let t = 0; t < cloth.tris.length; t += 3) {
    for (let k = 0; k < 3; k++) {
      const a = cloth.tris[t + k], b = cloth.tris[t + ((k + 1) % 3)], c = cloth.tris[t + ((k + 2) % 3)];
      const lo = Math.min(a, b), hi = Math.max(a, b), key = lo * n + hi;
      const opp = firstOpp.get(key);
      if (opp === undefined) {
        firstOpp.set(key, c);
        if (free[a] || free[b]) edges.push(a, b);
      } else if (opp !== c && (free[opp] || free[c])) bends.push(opp, c);
    }
  }
  const E = Uint32Array.from(edges), nE = E.length / 2, rest = new Float32Array(nE);
  for (let e = 0; e < nE; e++) {
    const a = E[e * 2] * 3, b = E[e * 2 + 1] * 3;
    rest[e] = Math.hypot(P0[a] - P0[b], P0[a + 1] - P0[b + 1], P0[a + 2] - P0[b + 2]);
  }
  const Bd = Uint32Array.from(bends), nB = Bd.length / 2, bendRest = new Float32Array(nB);
  for (let e = 0; e < nB; e++) {
    const a = Bd[e * 2] * 3, b = Bd[e * 2 + 1] * 3;
    bendRest[e] = Math.hypot(P0[a] - P0[b], P0[a + 1] - P0[b + 1], P0[a + 2] - P0[b + 2]);
  }
  const X = new Float32Array(n * 3);
  const S = new Float32Array(n * 3), prevS = new Float32Array(n * 3);
  const solve = (I: Uint32Array, R: Float32Array, count: number, k: number) => {
    for (let e = 0; e < count; e++) {
      const a = I[e * 2], b = I[e * 2 + 1];
      const fa = free[a], fb = free[b];
      const ax = a * 3, bx = b * 3;
      const dx = X[bx] - X[ax], dy = X[bx + 1] - X[ax + 1], dz = X[bx + 2] - X[ax + 2];
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1e-9;
      const diff = k * (d - R[e]) / d;
      const wa = fa ? (fb ? 0.5 : 1) : 0, wb = fb ? (fa ? 0.5 : 1) : 0;
      X[ax] += dx * diff * wa; X[ax + 1] += dy * diff * wa; X[ax + 2] += dz * diff * wa;
      X[bx] -= dx * diff * wb; X[bx + 1] -= dy * diff * wb; X[bx + 2] -= dz * diff * wb;
    }
  };
  // Écart au repos à la peau (couches superposées : volants, doublure) : conservé par les collisions.
  const layer = new Float32Array(n);
  {
    let a0 = Infinity, b0 = Infinity, c0 = Infinity, a1 = -Infinity, b1 = -Infinity, c1 = -Infinity;
    for (let v = 0; v < n; v++) {
      if (!free[v]) continue;
      const o = v * 3;
      a0 = Math.min(a0, P0[o]); b0 = Math.min(b0, P0[o + 1]); c0 = Math.min(c0, P0[o + 2]);
      a1 = Math.max(a1, P0[o]); b1 = Math.max(b1, P0[o + 1]); c1 = Math.max(c1, P0[o + 2]);
    }
    const BP = body.src.pos, BN = body.src.normal, sel: number[] = [];
    for (let b = 0; b < body.src.count; b++) {
      const x = BP[b * 3], y = BP[b * 3 + 1], z = BP[b * 3 + 2];
      if (x > a0 - 0.06 && x < a1 + 0.06 && y > b0 - 0.06 && y < b1 + 0.06 && z > c0 - 0.06 && z < c1 + 0.06) sel.push(b);
    }
    const sp = new Float32Array(sel.length * 3);
    sel.forEach((b, i) => { sp[i * 3] = BP[b * 3]; sp[i * 3 + 1] = BP[b * 3 + 1]; sp[i * 3 + 2] = BP[b * 3 + 2]; });
    const bg = new PointGrid(sp, sel.length, 0.03);
    for (let v = 0; v < n; v++) {
      if (!free[v]) continue;
      const o = v * 3, i = bg.nearest(P0[o], P0[o + 1], P0[o + 2], 0.06);
      if (i < 0) { layer[v] = T.layerMax; continue; }
      const b = sel[i];
      const sd = (P0[o] - BP[b * 3]) * BN[b * 3] + (P0[o + 1] - BP[b * 3 + 1]) * BN[b * 3 + 1] + (P0[o + 2] - BP[b * 3 + 2]) * BN[b * 3 + 2];
      layer[v] = Math.min(T.layerMax, Math.max(0, sd));
    }
  }
  // Corps utile : au repos, tout ce qui est sous le haut de la partie libre (+ 15 cm) — jambes, hanches, bras et mains
  // (ils peuvent venir se poser sur la jupe). Seuls ces sommets sont skinnés à chaque étape.
  let topFree = -Infinity;
  for (let v = 0; v < n; v++) if (free[v]) topFree = Math.max(topFree, P0[v * 3 + 1]);
  const subsetArr: number[] = [];
  for (let b = 0; b < body.src.count; b++) if (body.src.pos[b * 3 + 1] < topFree + 0.15) subsetArr.push(b);
  const subset = Int32Array.from(subsetArr), nSub = subset.length;
  // Corps posé (étape courante), sous-ensemble proche du tissu, grille.
  const bodyPos = new Float32Array(nSub * 3), bodyNrm = new Float32Array(nSub * 3);
  let bp = new Float32Array(0), bn = new Float32Array(0), grid = new PointGrid(bp, 0, 0.025);
  const rebuild = () => {
    let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
    for (let v = 0; v < n; v++) {
      if (!free[v]) continue;
      const o = v * 3;
      x0 = Math.min(x0, X[o]); y0 = Math.min(y0, X[o + 1]); z0 = Math.min(z0, X[o + 2]);
      x1 = Math.max(x1, X[o]); y1 = Math.max(y1, X[o + 1]); z1 = Math.max(z1, X[o + 2]);
    }
    const keep: number[] = [];
    for (let b = 0; b < nSub; b++) {
      const x = bodyPos[b * 3], y = bodyPos[b * 3 + 1], z = bodyPos[b * 3 + 2];
      if (x > x0 - 0.12 && x < x1 + 0.12 && y > y0 - 0.25 && y < y1 + 0.12 && z > z0 - 0.12 && z < z1 + 0.12) keep.push(b);
    }
    bp = new Float32Array(keep.length * 3); bn = new Float32Array(keep.length * 3);
    keep.forEach((b, i) => {
      bp[i * 3] = bodyPos[b * 3]; bp[i * 3 + 1] = bodyPos[b * 3 + 1]; bp[i * 3 + 2] = bodyPos[b * 3 + 2];
      bn[i * 3] = bodyNrm[b * 3]; bn[i * 3 + 1] = bodyNrm[b * 3 + 1]; bn[i * 3 + 2] = bodyNrm[b * 3 + 2];
    });
    grid = new PointGrid(bp, keep.length, 0.025);
  };
  const gap = 0.004;
  const near = new Int32Array(n).fill(-1);
  let pass = 0;
  const collide = (seatOn: boolean, every = 1) => {
    const refresh = pass++ % every === 0;
    for (let v = 0; v < n; v++) {
      if (!free[v]) continue;
      const o = v * 3;
      if (refresh) near[v] = grid.nearest(X[o], X[o + 1], X[o + 2], 0.05);
      const b = near[v];
      if (b >= 0) {
        const nx = bn[b * 3], ny = bn[b * 3 + 1], nz = bn[b * 3 + 2];
        const s = (X[o] - bp[b * 3]) * nx + (X[o + 1] - bp[b * 3 + 1]) * ny + (X[o + 2] - bp[b * 3 + 2]) * nz;
        const want = gap + layer[v];
        if (s < want && s > -0.05) { const p = want - s; X[o] += p * nx; X[o + 1] += p * ny; X[o + 2] += p * nz; }
      }
      const under = b >= 0 && bn[b * 3 + 1] < -0.25 && bp[b * 3 + 1] > X[o + 1] - 0.01;
      if (seat && seatOn && !under) {
        const dx = X[o] - seat.x, dz = X[o + 2] - seat.z, rr = Math.sqrt(dx * dx + dz * dz);
        const top = seat.height + gap + layer[v];
        if (rr < seat.radius + gap && X[o + 1] < top) {
          if (top - X[o + 1] < 0.06) X[o + 1] = top;
          else { const k = (seat.radius + gap) / (rr || 1e-6); X[o] = seat.x + dx * k; X[o + 2] = seat.z + dz * k; }
        }
      }
      const fl = 0.003 + layer[v];
      if (X[o + 1] < fl) X[o + 1] = fl;
    }
  };
  const lap = () => { const now = performance.now(); spent += now - tLap; tLap = now; };
  const resume = () => { tLap = performance.now(); };
  // Départ : pose de repos, tissu tel qu'il a été ajusté au corps.
  skinCloth(cloth, firstR, frames[0], prevS);
  X.set(prevS);
  // Balayage : le corps s'anime de la pose de repos à la pose voulue.
  for (let k = 1; k <= K; k++) {
    const tk = k / K;
    skinSubset(body.src, frames[k], subset, bodyPos, bodyNrm);
    skinCloth(cloth, firstR, frames[k], S);
    // Partie libre portée par le bassin (mouvement rigide d'un os) : le mélange des poids cuisse gauche / droite /
    // bassin écraserait le devant de la jupe dans le pli de l'aine ; ce sont les cuisses qui doivent la pousser.
    if (carryBone >= 0) {
      _m.fromArray(frames[k - 1], carryBone * 16).invert();
      _m2.fromArray(frames[k], carryBone * 16).multiply(_m);
    }
    const D = _m2.elements;
    for (let v = 0; v < n; v++) {
      const o = v * 3;
      if (!free[v]) { X[o] = S[o]; X[o + 1] = S[o + 1]; X[o + 2] = S[o + 2]; continue; }
      if (carryBone >= 0) {
        const x = X[o], y = X[o + 1], z = X[o + 2];
        X[o] = D[0] * x + D[4] * y + D[8] * z + D[12];
        X[o + 1] = D[1] * x + D[5] * y + D[9] * z + D[13];
        X[o + 2] = D[2] * x + D[6] * y + D[10] * z + D[14];
      } else {
        X[o] += S[o] - prevS[o]; X[o + 1] += S[o + 1] - prevS[o + 1]; X[o + 2] += S[o + 2] - prevS[o + 2];
      }
    }
    prevS.set(S);
    rebuild();
    pass = 0;
    for (let it = 0; it < T.subIters; it++) {
      for (let v = 0; v < n; v++) if (free[v]) X[v * 3 + 1] -= T.gravity * 0.5;
      for (let p = 0; p < T.passes; p++) { solve(E, rest, nE, 1); solve(Bd, bendRest, nB, T.bend); }
      collide(tk > 0.6, 2);
    }
    lap();
    yield;
    resume();
  }
  // Repos dans la pose voulue : gravité décroissante, contraintes, collisions (siège compris).
  pass = 0;
  for (let it = 0; it < T.settle; it++) {
    const g = T.gravity * (1 - it / T.settle);
    for (let v = 0; v < n; v++) if (free[v]) X[v * 3 + 1] -= g;
    for (let p = 0; p < T.passes; p++) { solve(E, rest, nE, 1); solve(Bd, bendRest, nB, T.bend); }
    collide(true, 4);
    if (it % 4 === 3) { lap(); yield; resume(); }
  }
  // Lissage de Taubin puis dernières collisions.
  {
    const deg = new Float32Array(n);
    for (let e = 0; e < nE; e++) { deg[E[e * 2]]++; deg[E[e * 2 + 1]]++; }
    const acc = new Float32Array(n * 3);
    const smooth = (f: number) => {
      acc.fill(0);
      for (let e = 0; e < nE; e++) {
        const a = E[e * 2] * 3, b = E[e * 2 + 1] * 3;
        for (let c = 0; c < 3; c++) { const d = X[b + c] - X[a + c]; acc[a + c] += d; acc[b + c] -= d; }
      }
      for (let v = 0; v < n; v++) {
        if (!free[v] || !deg[v]) continue;
        const o = v * 3, w = f / deg[v];
        X[o] += acc[o] * w; X[o + 1] += acc[o + 1] * w; X[o + 2] += acc[o + 2] * w;
      }
    };
    for (let i = 0; i < T.smooth; i++) { smooth(0.5); smooth(-0.53); }
    pass = 0;
    collide(true);
  }
  // Contrôle qualité : tissu enfoncé dans le corps (sonde large : on voit aussi le tissu au cœur d'une cuisse) ou
  // déchiré (arêtes très étirées) : on renonce.
  const probe = new PointGrid(bp, bp.length / 3, 0.05);
  let inside = 0, torn = 0, nFree = 0;
  let bad = false;
  for (let v = 0; v < n; v++) {
    if (!free[v]) continue;
    nFree++;
    if (!Number.isFinite(X[v * 3] + X[v * 3 + 1] + X[v * 3 + 2])) { bad = true; break; }
    const o = v * 3, b = probe.nearest(X[o], X[o + 1], X[o + 2], T.insideProbe);
    if (b < 0) continue;
    const s = (X[o] - bp[b * 3]) * bn[b * 3] + (X[o + 1] - bp[b * 3 + 1]) * bn[b * 3 + 1] + (X[o + 2] - bp[b * 3 + 2]) * bn[b * 3 + 2];
    if (s < -0.006) inside++;
  }
  let nCounted = 0;
  for (let e = 0; e < nE; e++) {
    if (rest[e] < 0.003) continue;
    nCounted++;
    const a = E[e * 2] * 3, b = E[e * 2 + 1] * 3;
    if (Math.hypot(X[b] - X[a], X[b + 1] - X[a + 1], X[b + 2] - X[a + 2]) > rest[e] * T.tear + 0.002) torn++;
  }
  lap();
  lastDrapeStats.inside = inside / Math.max(1, nFree);
  lastDrapeStats.torn = torn / Math.max(1, nCounted);
  lastDrapeStats.rejected = bad || lastDrapeStats.inside > T.maxInside || lastDrapeStats.torn > T.maxTorn;
  lastDrapeStats.ms = spent;
  if (lastDrapeStats.rejected) return null;
  // Retour dans l'espace de liaison (matrices de la pose voulue).
  const M = frames[K];
  const out = new Float32Array(P0);
  for (let v = 0; v < n; v++) {
    if (!free[v] || firstR[v] < 0) continue;
    vertexMatrix(M, cloth.skinIndex, cloth.skinWeight, firstR[v], _e);
    _m.fromArray(_e).invert();
    const e = _m.elements, x = X[v * 3], y = X[v * 3 + 1], z = X[v * 3 + 2];
    out[v * 3] = e[0] * x + e[4] * y + e[8] * z + e[12];
    out[v * 3 + 1] = e[1] * x + e[5] * y + e[9] * z + e[13];
    out[v * 3 + 2] = e[2] * x + e[6] * y + e[10] * z + e[14];
  }
  return out;
}
